import { Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import DeleteIcon from "../../../../../assets/images/icons/DeleteIcon";
import { EditIcon } from "../../../../../assets/images/icons/EditIcon";
import { UploadIcon } from "../../../../../assets/images/icons/UploadIcon";
import BriaIconButton from "../../../../../components/common/BriaIconButton/BriaIconButton";
import ImageUploader from "../../../../../components/common/ImageUploader/ImageUploader";
import StoppableButton from "../../../../../components/common/StoppableButton/StoppableButton";
import { useAppStore } from "../../../../../hooks/useStores";
import { uploadFileToS3 } from "../../../../../utils/uploadFileToS3";
import VideoEraserPopup from "./Popup/VideoEraserPopup";
import styles from "./VideoEraserConfig.module.scss";

const VideoEraserConfig = () => {
	const { t } = useTranslation("video_editing", { keyPrefix: "lab.config.tools.erase.config" });
	const { videoEditingStore, videoGenerationStore } = useAppStore();
	const [openMaskPopup, setOpenMaskPopup] = useState(false);

	const disabledCreateMask = !videoEditingStore.config.video || !!videoEditingStore.errors.erase?.fps;

	const handleCreateMaskClick = async () => {
		setOpenMaskPopup(true);
	};

	const handleEditMaskClick = async (e: React.MouseEvent<SVGElement | HTMLButtonElement>) => {
		e.preventDefault();
		setOpenMaskPopup(true);
	};

	const handleDeleteMaskClick = async (e: React.MouseEvent<SVGElement | HTMLButtonElement>) => {
		e.stopPropagation();
		e.preventDefault();
		videoEditingStore.handleConfigChange("tool_config", { erase: { mask: "" } });
		videoGenerationStore.mask_url = "";
		videoGenerationStore.merged_video_url = "";
	};

	const MaskAppliedComponent = () => {
		const [loading, setLoading] = useState(false);
		const [loadingProgress, setLoadingProgress] = useState<number | undefined>(undefined);

		const handlePreviewMask = async () => {
			await videoGenerationStore.previewMask();
			videoEditingStore.handleConfigChange("tool_config", {
				erase: { mask: videoGenerationStore.mask_url },
			});
		};

		const handleUploadMask = async (e: ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			if (!file) return;
			try {
				setLoading(true);
				const maskUrl = await uploadFileToS3(file, "video", setLoadingProgress);
				videoGenerationStore.handleSegmentConfigChange("mask_url", maskUrl);
				await handlePreviewMask();
			} catch (error: any) {
				throw new Error(`Error uploading mask: ${error?.message || error?.toString()}`);
			} finally {
				setLoadingProgress(undefined);
				setLoading(false);
			}
		};

		return (
			<ImageUploader
				src={videoGenerationStore.maskToDisplay}
				description={t("mediaUploader.description")}
				icon={<UploadIcon />}
				showTitle
				itemTitle={t("mediaUploader.title")}
				itemDescription={t("mediaUploader.description")}
				customOverlay={() => (
					<>
						<BriaIconButton
							square
							className={styles.customOverlayButton}
							onClick={(e) => handleEditMaskClick(e)}
							title={t("mediaUploader.edit")}
						>
							{<EditIcon />}
						</BriaIconButton>
						<BriaIconButton
							square
							className={styles.customOverlayButton}
							onClick={handleDeleteMaskClick}
							title={t("mediaUploader.delete")}
						>
							{<DeleteIcon />}
						</BriaIconButton>
					</>
				)}
				customOverlayClassName={styles.customOverlay}
				className={styles.maskUploader}
				inputProps={{ accept: "video/*" }}
				onClick={(e) => e.preventDefault()}
				onUpload={handleUploadMask}
				loading={loading}
				loadingProgress={loadingProgress}
			/>
		);
	};

	return (
		<>
			{videoGenerationStore.maskToDisplay ? (
				<MaskAppliedComponent />
			) : (
				<Box className={styles.container}>
					<Box className={styles.createMaskContainer}>
						<Typography className={styles.description}>{t("description")}</Typography>
						<StoppableButton
							condition={videoGenerationStore.isGeneratingMask}
							onStopButtonClick={() => videoGenerationStore.abortVideoSegmentation()}
							onButtonClick={handleCreateMaskClick}
							buttonLabel={t("createMaskButton")}
							stopButtonLabel={t("stopCreateMask")}
							disabled={disabledCreateMask}
							disabledTooltip={t("disabledTooltip")}
							className={styles.createMaskBtn}
						/>
					</Box>
				</Box>
			)}
			<VideoEraserPopup open={openMaskPopup} onClose={() => setOpenMaskPopup(false)} />
		</>
	);
};

export default observer(VideoEraserConfig);
